import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { DragDropProvider, type DragEndEvent } from '@dnd-kit/react'
import { GripVerticalIcon, Upload } from 'lucide-react'
import { useDebounce } from '@app/hooks/useDebounce'
import { appLink } from '@app/config/appLink'
import { toast } from '@app/components/ui/Toast/toast'
import { Button } from '@/components/ui/button'
import {
  BreadcrumbLink,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { DocumentsToolbar } from '../components/documents-toolbar'
import { DocumentsTable } from '../components/documents-table'
import { DocumentsPagination } from '../components/documents-pagination'
import { DocumentsFiltersSheet } from '../components/documents-filters-sheet'
import { DocumentPreviewDialog } from '../components/document-preview-dialog'
import { DocumentDeleteDialog } from '../components/document-delete-dialog'
import { UploadWizardDialog } from '../components/upload-wizard/upload-wizard-dialog'
import { EditWizardDialog } from '../components/upload-wizard/edit-wizard-dialog'
import { FolderFormDialog } from '../components/folder-form-dialog'
import { FolderDeleteDialog } from '../components/folder-delete-dialog'
import { FoldersSidebar } from '../components/folders-sidebar'
import { DocumentsWordmark } from '../components/documents-wordmark'
import { DocumentCountPill } from '../components/document-count-pill'
import { useDeleteDocument, useFetchDocument, useFetchDocuments } from '../hooks/useDocuments'
import { useDocumentsUrlState } from '../hooks/useDocumentsUrlState'
import {
  useAddDocumentToFolder,
  useCreateFolder,
  useDeleteFolder,
  useFetchFolders,
  useRemoveDocumentFromFolder,
  useUpdateFolder,
} from '../hooks/useFolders'
import { DEFAULT_FILTERS, DEFAULT_VISIBLE_COLUMNS } from '../documents.constants'
import { DocumentColumnKey, DocumentsFilters, EvidenceDocument } from '../documents.types'
import { Folder } from '../collections.types'
import { isDuplicateFolderNameError } from '../service/collections.service'
import { countActiveSheetFilters } from '../documents.utils'

export const DocumentsLibraryPage = () => {
  const { id: routeDocumentId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { query, setQuery } = useDocumentsUrlState()
  // Debounced before pushing to the URL, so keystrokes don't spam history entries.
  const [searchInput, setSearchInput] = useState(query.search)
  const [visibleColumns, setVisibleColumns] = useState<DocumentColumnKey[]>(DEFAULT_VISIBLE_COLUMNS)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<EvidenceDocument | null>(null)
  const [uploadWizardOpen, setUploadWizardOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<EvidenceDocument | null>(null)
  // Distinct from folderFormOpen so the dialog keeps showing the folder name during its close animation.
  const [folderFormTarget, setFolderFormTarget] = useState<Folder | null>(null)
  const [folderFormOpen, setFolderFormOpen] = useState(false)
  const [folderNameError, setFolderNameError] = useState<string | null>(null)
  const [createNameError, setCreateNameError] = useState<string | null>(null)
  const [folderDeleteTarget, setFolderDeleteTarget] = useState<Folder | null>(null)

  const debouncedSearch = useDebounce(searchInput, 300)

  useEffect(() => {
    if (debouncedSearch !== query.search) setQuery({ search: debouncedSearch, page: 1 })
    // Only debouncedSearch settling should trigger this, not query/setQuery changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // Keeps the search box in sync with the URL (browser back/forward). Doesn't loop with the
  // effect above, which only fires when debouncedSearch differs from query.search.
  useEffect(() => {
    setSearchInput(query.search)
  }, [query.search])

  const { data, isLoading, isFetching } = useFetchDocuments(query)
  const deleteDocument = useDeleteDocument()

  const { data: folders = [], isLoading: foldersLoading } = useFetchFolders()
  const createFolder = useCreateFolder()
  const updateFolder = useUpdateFolder()
  const deleteFolder = useDeleteFolder()
  const addDocumentToFolder = useAddDocumentToFolder()
  const removeDocumentFromFolder = useRemoveDocumentFromFolder()

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return
    const { source, target } = event.operation
    const documentId = source?.data?.documentId as string | undefined
    const documentTitle = (source?.data?.title as string | undefined) ?? ''
    const currentFolderId = (source?.data?.collectionId as string | null | undefined) ?? null
    if (!documentId || !target) return

    const folderId = target.data?.folderId as string | undefined
    if (folderId) {
      if (folderId === currentFolderId) return
      const folderName = folders.find((f) => f.id === folderId)?.name ?? ''
      addDocumentToFolder.mutate({ folderId, documentId, documentTitle, folderName })
      return
    }

    if (target.data?.dropzone === 'all' && currentFolderId) {
      const folderName = folders.find((f) => f.id === currentFolderId)?.name ?? ''
      removeDocumentFromFolder.mutate({
        folderId: currentFolderId,
        documentId,
        documentTitle,
        folderName,
      })
    }
  }

  // Fetched independently of the list above since the target document may not be on the
  // current page, or may not match the current filters at all.
  const routeDocumentQuery = useFetchDocument(routeDocumentId, !!routeDocumentId)

  useEffect(() => {
    if (!routeDocumentQuery.isError) return
    const status = isAxiosError(routeDocumentQuery.error)
      ? routeDocumentQuery.error.response?.status
      : undefined
    toast({
      description:
        status === 403
          ? "You don't have access to this document."
          : 'That document could not be found.',
      position: 'top-right',
      type: 'error',
    })
    navigate({ pathname: appLink.documents.path, search: location.search }, { replace: true })
    // navigate/location.search intentionally excluded: including them risks re-running this on unrelated navigations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDocumentQuery.isError, routeDocumentQuery.error])

  const updateFilters = (next: Partial<DocumentsFilters>) => {
    setQuery({ ...next, page: 1 })
  }

  const handleFolderFormSubmit = async (values: { name: string; description: string | null }) => {
    if (!folderFormTarget) return
    setFolderNameError(null)
    try {
      await updateFolder.mutateAsync({ id: folderFormTarget.id, payload: values })
      setFolderFormOpen(false)
    } catch (error) {
      // Other failures already get a toast from the mutation's own onError.
      if (isDuplicateFolderNameError(error)) {
        setFolderNameError('A collection with this name already exists.')
      }
    }
  }

  const handleCreateFolder = async (name: string) => {
    setCreateNameError(null)
    try {
      await createFolder.mutateAsync({ name, description: null })
      return true
    } catch (error) {
      // Other failures already get a toast from the mutation's own onError.
      if (isDuplicateFolderNameError(error)) {
        setCreateNameError('A collection with this name already exists.')
      }
      return false
    }
  }

  const handleFolderDeleteConfirm = () => {
    if (!folderDeleteTarget) return
    // Reset the filter if it's pointing at the folder being deleted.
    if (query.collectionId === folderDeleteTarget.id) updateFilters({ collectionId: null })
    deleteFolder.mutate(folderDeleteTarget.id)
    setFolderDeleteTarget(null)
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    deleteDocument.mutate(deleteTarget.id)
    setDeleteTarget(null)
  }

  const closePreview = () => {
    if (routeDocumentId) {
      navigate({ pathname: appLink.documents.path, search: location.search }, { replace: true })
    }
  }

  const activeFolder = folders.find((f) => f.id === query.collectionId) ?? null

  return (
    <div className="h-[calc(100vh-3.5rem)] overflow-y-auto flex flex-col">
      <div className="flex flex-col gap-8 px-8 pt-4 pb-4 justify-between sm:flex-row sm:items-center">
        <DocumentsWordmark className="h-[28px] w-auto shrink-0" />
        <Button onClick={() => setUploadWizardOpen(true)}>
          <Upload className="h-4 w-4" />
          Upload
        </Button>
      </div>

      <div className="flex-1 bg-muted/40 px-8 pb-8">
        <div className="mx-auto">
          <DragDropProvider onDragEnd={handleDragEnd}>
            <div className="flex items-start gap-6">
              <FoldersSidebar
                folders={folders}
                selectedFolderId={query.collectionId}
                onSelect={(collectionId) => updateFilters({ collectionId })}
                onCreate={handleCreateFolder}
                createNameError={createNameError}
                onClearCreateNameError={() => setCreateNameError(null)}
                onRename={(folder) => {
                  setFolderFormTarget(folder)
                  setFolderNameError(null)
                  setFolderFormOpen(true)
                }}
                onDelete={setFolderDeleteTarget}
                isLoading={foldersLoading}
              />

              <div className="flex-1 min-w-0 pt-6 flex flex-col gap-4">
                <div className="flex gap-4">
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink onClick={() => updateFilters({ collectionId: undefined })}>
                        <button>
                          All Documents{' '}
                          {!activeFolder && <DocumentCountPill count={data?.total_count ?? 0} />}
                        </button>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {activeFolder && (
                      <>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbPage>
                            {activeFolder.name} <DocumentCountPill count={data?.total_count ?? 0} />
                          </BreadcrumbPage>
                        </BreadcrumbItem>
                      </>
                    )}
                  </BreadcrumbList>
                </div>

                <DocumentsToolbar
                  search={searchInput}
                  onSearchChange={setSearchInput}
                  category={query.category}
                  onCategoryChange={(category) => updateFilters({ category })}
                  tags={data?.tags ?? []}
                  selectedTags={query.tags}
                  onTagsChange={(tags) => updateFilters({ tags })}
                  tagsLoading={isLoading || isFetching}
                  visibleColumns={visibleColumns}
                  onVisibleColumnsChange={setVisibleColumns}
                  onOpenFilters={() => setFiltersOpen(true)}
                  activeFilterCount={countActiveSheetFilters(query, DEFAULT_FILTERS)}
                  collectionsFilter={{
                    folders,
                    selectedFolderId: query.collectionId,
                    onSelect: (collectionId) => updateFilters({ collectionId }),
                  }}
                />

                <p className="hidden xl:flex items-center gap-1.5 text-sm text-muted-foreground">
                  <GripVerticalIcon className="h-3.5 w-3.5" />
                  Drag a document by its row handle onto a collection in the left panel to file it.
                </p>

                <DocumentsTable
                  documents={data?.documents ?? []}
                  visibleColumns={visibleColumns}
                  sort={query.sort}
                  onSortChange={(next) => setQuery({ sort: next, page: 1 })}
                  search={location.search}
                  onEdit={setEditTarget}
                  onDelete={setDeleteTarget}
                  folders={folders}
                  activeCollectionId={query.collectionId}
                  // isFetching alone (e.g. background refetch after add/remove-folder) must not blank
                  // the table — keepPreviousData is what keeps existing rows on screen through that.
                  isLoading={isLoading}
                />

                {data && data.total_count > 0 && (
                  <DocumentsPagination
                    page={query.page}
                    pageSize={query.pageSize}
                    pageCount={data.page_count}
                    totalCount={data.total_count}
                    rowsShown={data.documents.length}
                    onPageChange={(page) => setQuery({ page })}
                    onPageSizeChange={(pageSize) => setQuery({ pageSize, page: 1 })}
                  />
                )}
              </div>
            </div>
          </DragDropProvider>
        </div>
      </div>

      <DocumentsFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={query}
        onApply={(next) => updateFilters(next)}
      />

      <DocumentPreviewDialog
        document={routeDocumentQuery.data ?? null}
        open={!!routeDocumentId}
        onOpenChange={(open) => !open && closePreview()}
      />

      <DocumentDeleteDialog
        document={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteDocument.isPending}
      />

      <FolderFormDialog
        folder={folderFormTarget}
        open={folderFormOpen}
        onOpenChange={setFolderFormOpen}
        onSubmit={handleFolderFormSubmit}
        isSubmitting={createFolder.isPending || updateFolder.isPending}
        nameError={folderNameError}
      />

      <FolderDeleteDialog
        folder={folderDeleteTarget}
        open={folderDeleteTarget !== null}
        onOpenChange={(open) => !open && setFolderDeleteTarget(null)}
        onConfirm={handleFolderDeleteConfirm}
        isDeleting={deleteFolder.isPending}
      />

      <UploadWizardDialog open={uploadWizardOpen} onOpenChange={setUploadWizardOpen} />

      <EditWizardDialog
        document={editTarget}
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
    </div>
  )
}
